import React, { createContext, useContext, useEffect, useState } from 'react';
import { getDatabase, ref, onValue, set, remove } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { migrateUserNotifications } from '../services/notificationMigrationService';

const NotificationsContext = createContext();

export const useNotifications = () => {
  return useContext(NotificationsContext);
};

/**
 * Show a desktop pop-up for a notification that arrived while the tab was in
 * the background. Only when the browser has already been given permission
 * (the To Do page asks for it), and never for the inbox as first loaded.
 */
const announce = (notification) => {
  try {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    if (typeof document !== 'undefined' && !document.hidden) return;
    // The To Do page raises its own pop-up for a reminder as it fires.
    if (notification.requestType === 'Task' && notification.title === 'Reminder') return;
    const popup = new Notification(notification.title || 'New notification', {
      body: notification.message || '',
      tag: notification.id,
    });
    popup.onclick = () => {
      window.focus();
      popup.close();
    };
  } catch {
    /* A pop-up is a bonus; the inbox is the real record. */
  }
};

export const NotificationsProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const auth = getAuth();

  useEffect(() => {
    const db = getDatabase();
    let unsubscribeNotifications = () => {};

    const unsubscribeAuth = auth.onAuthStateChanged((user) => {
      // Drop the previous user's listener before starting the next one.
      unsubscribeNotifications();
      unsubscribeNotifications = () => {};
      setNotifications([]);
      if (!user) return;

      // Anything still at the old users/{uid}/notifications path moves over.
      migrateUserNotifications(user.uid).catch((err) =>
        console.error('Could not move notifications to the new path', err)
      );

      // What each notification looked like last time, to tell a new or
      // changed one from the ones that were already there.
      let seen = null;

      unsubscribeNotifications = onValue(ref(db, `notifications/${user.uid}`), (snapshot) => {
        const data = snapshot.val();
        // The key is the id. A stored `id` field must not override it.
        const list = data ? Object.keys(data).map((key) => ({ ...data[key], id: key })) : [];

        if (seen) {
          list
            .filter((n) => !n.read && seen.get(n.id) !== n.timestamp)
            .forEach(announce);
        }
        seen = new Map(list.map((n) => [n.id, n.timestamp]));

        setNotifications(list);
      });
    });

    return () => {
      unsubscribeAuth();
      unsubscribeNotifications();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markAsRead = (id) => {
    const user = auth.currentUser;
    if (!user) return;
    const db = getDatabase();
    const notificationRef = ref(db, `notifications/${user.uid}/${id}/read`);
    set(notificationRef, true);
  };

  const markAsUnread = (id) => {
    const user = auth.currentUser;
    if (!user) return;
    const db = getDatabase();
    const notificationRef = ref(db, `notifications/${user.uid}/${id}/read`);
    set(notificationRef, false);
  };

  const deleteNotification = (id) => {
    const user = auth.currentUser;
    if (!user) return;
    const db = getDatabase();
    const notificationRef = ref(db, `notifications/${user.uid}/${id}`);
    remove(notificationRef);
  };

  return (
    <NotificationsContext.Provider value={{ notifications, markAsRead, markAsUnread, deleteNotification }}>
      {children}
    </NotificationsContext.Provider>
  );
};
